import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { addCansManually, removeCansManually } from '../../actions/can';
import { getProducts } from '../../actions/product';
import { 
    Card,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Spinner
} from 'react-bootstrap';
import {
    PlusCircle,
    MinusCircle,
    User,
    Package,
    Droplets,
    ArrowLeftCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const CanManagement = ({ addCansManually, removeCansManually, products, getProducts }) => {
    const [mode, setMode] = useState('add'); // 'add' or 'remove'
    const [formData, setFormData] = useState({
        userId: '',
        productId: '',
        cans: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        if (!products || products.length === 0) {
            getProducts();
        }
    }, [products, getProducts]);

    const onChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const validate = () => {
        if (!formData.userId || !formData.productId || !formData.cans) {
            setLocalError('User ID, Product ID and Cans are required.');
            return false;
        }
        const cansNumber = Number(formData.cans);
        if (!Number.isInteger(cansNumber) || cansNumber <= 0) {
            setLocalError('Cans must be a positive integer.');
            return false;
        }
        setLocalError('');
        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            userId: formData.userId.trim(),
            productId: formData.productId.trim(),
            cans: Number(formData.cans)
        };

        try {
            setIsSubmitting(true);
            if (mode === 'add') {
                await addCansManually(payload);
            } else {
                await removeCansManually(payload);
            }
            setFormData({
                userId: '',
                productId: '',
                cans: ''
            });
        } catch (err) {
            // Error alerts are handled by actions
        } finally {
            setIsSubmitting(false);
        }
    };

    const isAddMode = mode === 'add';

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-primary fw-bold">
                                    Can Balance Management
                                </h1>
                                <p className="text-muted mb-0">
                                    Manually add or remove cans for home delivery users
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mode Toggle */}
                <Row className="mb-4">
                    <Col md={6}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Droplets size={20} className="me-2 text-primary" />
                                        Operation Type
                                    </h5>
                                </div>
                                <div className="btn-group w-100" role="group">
                                    <Button
                                        variant={isAddMode ? 'success' : 'outline-success'}
                                        className="w-50 d-flex align-items-center justify-content-center"
                                        onClick={() => setMode('add')}
                                        disabled={isSubmitting}
                                    >
                                        <PlusCircle size={18} className="me-2" />
                                        Add Cans
                                    </Button>
                                    <Button
                                        variant={!isAddMode ? 'danger' : 'outline-danger'}
                                        className="w-50 d-flex align-items-center justify-content-center"
                                        onClick={() => setMode('remove')}
                                        disabled={isSubmitting}
                                    >
                                        <MinusCircle size={18} className="me-2" />
                                        Remove Cans
                                    </Button>
                                </div>
                                <p className="text-muted small mt-3 mb-0">
                                    {isAddMode
                                        ? 'Adds cans to the user and logs a credit entry in the can ledger.'
                                        : 'Removes cans from the user and logs a debit entry in the can ledger.'}
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Form */}
                <Row>
                    <Col md={8} lg={6}>
                        <Card className="shadow-sm border-0">
                            <Card.Header className={isAddMode ? 'bg-success text-white' : 'bg-danger text-white'}>
                                <h5 className="mb-0 d-flex align-items-center">
                                    {isAddMode ? (
                                        <PlusCircle size={18} className="me-2" />
                                    ) : (
                                        <MinusCircle size={18} className="me-2" />
                                    )}
                                    {isAddMode ? 'Add Cans Manually' : 'Remove Cans Manually'}
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {localError && (
                                    <Alert variant="danger" onClose={() => setLocalError('')} dismissible>
                                        {localError}
                                    </Alert>
                                )}
                                <Form onSubmit={onSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            <User size={16} className="me-2 text-primary" />
                                            User ID
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="userId"
                                            placeholder="Enter User ObjectId"
                                            value={formData.userId}
                                            onChange={onChange}
                                            disabled={isSubmitting}
                                            required
                                        />
                                        <Form.Text className="text-muted">
                                            Use the MongoDB ObjectId for the user (e.g., from the Users page).
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            <Package size={16} className="me-2 text-success" />
                                            Product
                                        </Form.Label>
                                        <Form.Select
                                            name="productId"
                                            value={formData.productId}
                                            onChange={onChange}
                                            disabled={isSubmitting}
                                            required
                                        >
                                            <option value="">Select water product</option>
                                            {(products || [])
                                                .filter((p) => !p.productType || p.productType === 'water')
                                                .map((product) => (
                                                    <option key={product._id} value={product._id}>
                                                        {product.brand || product.name} ({product.productType || 'water'})
                                                    </option>
                                                ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            The selected product&apos;s ObjectId will be sent to the API.
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold">
                                            <Droplets size={16} className="me-2 text-info" />
                                            Cans
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="cans"
                                            min="1"
                                            step="1"
                                            placeholder="Number of cans"
                                            value={formData.cans}
                                            onChange={onChange}
                                            disabled={isSubmitting}
                                            required
                                        />
                                        <Form.Text className="text-muted">
                                            Positive integer value as required by the API.
                                        </Form.Text>
                                    </Form.Group>

                                    <div className="d-flex justify-content-end">
                                        <Button
                                            type="submit"
                                            variant={isAddMode ? 'success' : 'danger'}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    {isAddMode ? (
                                                        <PlusCircle size={16} className="me-2" />
                                                    ) : (
                                                        <MinusCircle size={16} className="me-2" />
                                                    )}
                                                    {isAddMode ? 'Add Cans' : 'Remove Cans'}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

CanManagement.propTypes = {
    addCansManually: PropTypes.func.isRequired,
    removeCansManually: PropTypes.func.isRequired,
    products: PropTypes.array,
    getProducts: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    products: state.product.products
});

export default connect(mapStateToProps, { addCansManually, removeCansManually, getProducts })(CanManagement);


